import { useState, useEffect } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router-dom"
import { Text } from "@/components/Primitives/Text"
import { Button } from "@/components/Primitives/Button"
import { useConfig } from "@/hooks/useConfig"
import { QuestLayout } from "@/components/Complex/QuestLayout"
import { NextJourneyMissionInfo, useJourneyData } from "@/hooks/useJourneyData"
import { JourneyMission, JourneyStage } from "@/types/quests"
import { useStartMission } from "@/hooks/useStartMission"
import { QuestComplete } from "@/components/Complex/QuestComplete/questComplete"
import { isStageNavigable } from "@/lib/utils/stageNavigation"
import { MarkdownRenderer } from "@/components/Primitives/MarkdownRenderer"
import { queryKeys } from "@/lib/utils/query"
import { useQueryClient } from "@tanstack/react-query"
import { Card } from "@/components/Primitives/Card"
import { NotFoundPage } from "./NotFoundPage"
import { useJourneys } from "@/hooks/useJourneys"
import { isAuthenticated } from "@/hooks/useLocalStorage"
import { cn } from "@/lib/utils/utils"

export function QuestDetailPage() {
  return (
    <div className="-mx-2 w-full">
      <QuestDetailPageInternal />
    </div>
  )
}

function QuestDetailPageInternal() {
  const navigate = useNavigate()
  const { questId } = useParams<{ questId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useConfig()
  const queryClient = useQueryClient()
  const [accordionValue, setAccordionValue] = useState<string>("")

  const [viewMode, setViewMode] = useState(
    searchParams.get("view") || "details",
  )

  const hasExplicitlyChosenDetailsView =
    searchParams.has("view") && searchParams.get("view") === "details"

  const { startJourneyMission, isStarting } = useStartMission()
  const { journeys, loading: journeysLoading } = useJourneys(isAuthenticated())

  const journey = journeys.find((j) => j.quest.id === questId)
  const journeyId = journey?.id

  const {
    journey: journeyData,
    stages,
    currentStage,
    currentStageIndex,
    completedStageIds,
    nextJourneyMission,
    calculateStageProgress,
    loading,
    error,
    journeyProgress,
  } = useJourneyData(journeyId || "")

  const isAllComplete = journeyData && journeyData.state === "OK"

  const [displayedStage, setDisplayedStage] = useState<JourneyStage | null>(
    null,
  )

  useEffect(() => {
    if (accordionValue && stages) {
      const selectedStage = stages.find((stage) => stage.id === accordionValue)
      if (selectedStage) {
        setDisplayedStage(selectedStage)
      }
    } else if (currentStage) {
      setDisplayedStage(currentStage)
    }
  }, [accordionValue, stages, currentStage])

  const stageToDisplay = displayedStage || currentStage

  useEffect(() => {
    const viewParam = searchParams.get("view")
    if (viewParam) {
      setViewMode(viewParam)
    }
  }, [searchParams])

  useEffect(() => {
    if (!stages || stages.length === 0) return

    const stageIdParam = searchParams.get("stageId")
    if (stageIdParam) {
      const selectedStage = stages.find((stage) => stage.id === stageIdParam)

      if (selectedStage) {
        setAccordionValue(stageIdParam)
        setDisplayedStage(selectedStage)
      } else if (stages.length > 0) {
        const stageToShow = currentStage || stages[0]
        setDisplayedStage(stageToShow)
        setAccordionValue(stageToShow.id)
      }
    } else if (currentStage) {
      setDisplayedStage(currentStage)
      setAccordionValue(currentStage.id)
    }
  }, [stages, currentStage, searchParams])

  useEffect(() => {
    if (
      isAllComplete &&
      viewMode !== "complete" &&
      !hasExplicitlyChosenDetailsView &&
      !loading
    ) {
      setViewMode("complete")
      setSearchParams({ view: "complete" })
    }
  }, [
    isAllComplete,
    viewMode,
    questId,
    hasExplicitlyChosenDetailsView,
    loading,
    setSearchParams,
  ])

  const handleStageNavigable = (index: number) => {
    return isStageNavigable(index, stages, currentStageIndex, completedStageIds)
  }

  const handleStartQuest = (nextJourneyMission: NextJourneyMissionInfo) => {
    startJourneyMission(nextJourneyMission)
    queryClient.invalidateQueries({ queryKey: queryKeys.journeys })
  }

  if (journeysLoading || loading) {
    return (
      <Card className="p-4 bg-white rounded-lg">
        <div className="container mx-auto p-6">
          <Text variant="lead">{t("config_quests_loading")}</Text>
        </div>
      </Card>
    )
  }

  if (error || !journeyData || !journey) {
    return <NotFoundPage />
  }

  const footerContent = nextJourneyMission && (
    <Button
      onClick={() => handleStartQuest(nextJourneyMission)}
      disabled={isStarting}
      className="bg-purple-500 hover:bg-purple-600 text-neutral-50 w-full md:w-fit"
    >
      {isStarting
        ? t("config_mission_button_starting")
        : t("config_mission_button_start_next")}
    </Button>
  )

  if (viewMode === "complete") {
    return (
      <Card className="p-4 bg-white rounded-lg">
        <QuestComplete
          questId={questId!}
          questName={journeyData.quest.title}
          conclusionText={journeyData.quest.conclusion_text}
          stages={stages || []}
          onNextModule={() => {
            setViewMode("details")
            setSearchParams({ view: "details" })
          }}
        />
      </Card>
    )
  }

  const setStepper = (mission: JourneyMission, isLast: boolean) => {
    if (isLast) {
      return null
    }

    const color = mission.state === "OK" ? "bg-purple-500" : "bg-purple-100"

    return (
      <div
        className={`absolute left-[0.4375rem] top-0 bottom-[-1.5rem] w-0.5 ${color} z-0`}
      />
    )
  }

  return (
    <Card className="p-4 bg-white rounded-lg">
      <QuestLayout
        questName={journeyData.quest.title}
        stageName={stageToDisplay ? stageToDisplay.stage.title : ""}
        stages={stages || []}
        currentStageIndex={currentStageIndex}
        completedStages={completedStageIds}
        questId={questId!}
        onStageSelect={(index) => {
          if (stages && stages[index] && handleStageNavigable(index)) {
            const selectedStage = stages[index]

            setAccordionValue(selectedStage.id)
            setDisplayedStage(selectedStage)

            setSearchParams({
              view: "details",
              stageId: selectedStage.id,
            })
          }
        }}
        accordionValue={accordionValue}
        setAccordionValue={setAccordionValue}
        calculateStageProgress={calculateStageProgress}
        footerContent={footerContent}
        isStageNavigable={handleStageNavigable}
        journeyProgress={journeyProgress}
      >
        <div className="space-y-8">
          {stageToDisplay && (
            <div className="mb-6">
              <MarkdownRenderer
                content={stageToDisplay.stage.summary}
                className="mb-6"
              />
              <div>
                <div className="">
                  {stageToDisplay.journey_missions.map(
                    (journey_mission, missionIndex) => {
                      const isCompleted = journey_mission.state === "OK"
                      const isLast =
                        missionIndex ===
                        stageToDisplay.journey_missions.length - 1
                      const isNextMission =
                        nextJourneyMission &&
                        journey_mission.mission.id ===
                          nextJourneyMission.missionId

                      return (
                        <div key={journey_mission.id} className="relative">
                          {setStepper(journey_mission, isLast)}
                          <div className="relative z-10">
                            <div className="flex items-start mb-4">
                              <div className="mr-4 flex-shrink-0">
                                {isCompleted || missionIndex === 0 ? (
                                  <div className="h-4 w-4 rounded-full bg-purple-500 flex items-center justify-center" />
                                ) : (
                                  <div className="h-4 w-4 rounded-full bg-purple-100 flex items-center justify-center" />
                                )}
                              </div>

                              <div className={`flex-grow`}>
                                <div className="flex flex-col space-y-2">
                                  <div className="flex justify-between mb-4 ">
                                    <div className="pr-4">
                                      <Text
                                        variant="small"
                                        bold
                                        className={cn(
                                          "text-purple-500",
                                          !isCompleted &&
                                            !isNextMission &&
                                            "text-purple-300",
                                        )}
                                      >
                                        Mission {missionIndex + 1}
                                      </Text>
                                      <Text
                                        bold
                                        className={cn(
                                          "mt-2 text-xl",
                                          !isCompleted &&
                                            !isNextMission &&
                                            "text-gray-500",
                                        )}
                                      >
                                        {journey_mission.mission.title}
                                      </Text>
                                      <MarkdownRenderer
                                        content={
                                          journey_mission.mission.summary
                                        }
                                        className={cn(
                                          !isCompleted &&
                                            !isNextMission &&
                                            "text-gray-500",
                                        )}
                                      />
                                    </div>
                                  </div>

                                  {isCompleted && (
                                    <div className="flex justify-start">
                                      <Button
                                        data-testid="see-mission"
                                        onClick={() =>
                                          navigate(
                                            `/missions/${journey_mission.mission.id}`,
                                          )
                                        }
                                        className="px-4 py-2 text-xs border border-purple-500 text-purple-500 bg-white hover:bg-purple-50 rounded-md flex items-center gap-1 transition-colors"
                                        variant="outline"
                                        size="sm"
                                      >
                                        {t("config_mission_button_see")}
                                      </Button>
                                    </div>
                                  )}

                                  {!isCompleted && isNextMission && (
                                    <div className="flex justify-start">
                                      <Button
                                        onClick={() =>
                                          handleStartQuest(nextJourneyMission)
                                        }
                                        disabled={isStarting}
                                        className="px-4 py-2 text-xs bg-purple-500 hover:bg-purple-600 text-white rounded-md flex items-center gap-1 transition-colors"
                                        variant="default"
                                        size="sm"
                                      >
                                        {isStarting
                                          ? t("config_mission_button_starting")
                                          : t("config_mission_button_start")}
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    },
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </QuestLayout>
    </Card>
  )
}
